import Close from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';

import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { BulkSettlementCreateResult } from '~hooks/useSettlements/models';
import { getLineIneligibilityReasonLabel } from '~utils/lineIneligibilityReasonLabel';

interface SettlementsCreatedToasterProps {
  onClose: () => void;
  onViewSettlements?: () => void;
  bulkSettlementCreateResult: BulkSettlementCreateResult;
}

export function SettlementsCreatedToaster({
  onClose,
  onViewSettlements,
  bulkSettlementCreateResult,
}: SettlementsCreatedToasterProps) {
  const {
    createdCustomerSettlements,
    createdVendorSettlements,
    createdDriverSettlements,
    skippedLines,
  } = bulkSettlementCreateResult;

  const totalCount =
    createdCustomerSettlements.count +
    createdVendorSettlements.count +
    createdDriverSettlements.count;

  return (
    <Box sx={{ py: 2 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: 1,
        }}
      >
        <Typography sx={{ fontSize: '14px', fontWeight: 600, flex: 1 }}>
          {totalCount === 0
            ? t(
                'approvals.driver_pay.settlements.bulk_create_settlement.no_settlements_created',
              )
            : t(
                'approvals.driver_pay.settlements.bulk_create_settlement.settlements_created',
              )}
        </Typography>
        <IconButton
          size="small"
          onClick={(event) => {
            event.stopPropagation();
            onClose();
          }}
          sx={{ ml: 1 }}
        >
          <Close sx={{ fontSize: '12px' }} />
        </IconButton>
      </Box>

      {createdCustomerSettlements.count > 0 && (
        <Typography variant="body2">
          {t(
            'approvals.driver_pay.settlements.bulk_create_settlement.customer_settlements_count',
            { count: createdCustomerSettlements.count },
          )}
          {createdCustomerSettlements.totalAmount > 0 &&
            ` ($${createdCustomerSettlements.totalAmount.toFixed(2)})`}
        </Typography>
      )}

      {createdVendorSettlements.count > 0 && (
        <Typography variant="body2">
          {t(
            'approvals.driver_pay.settlements.bulk_create_settlement.vendor_settlements_count',
            { count: createdVendorSettlements.count },
          )}
          {createdVendorSettlements.totalAmount > 0 &&
            ` ($${createdVendorSettlements.totalAmount.toFixed(2)})`}
        </Typography>
      )}

      {createdDriverSettlements.count > 0 && (
        <Typography variant="body2">
          {t(
            'approvals.driver_pay.settlements.bulk_create_settlement.driver_settlements_count',
            { count: createdDriverSettlements.count },
          )}
          {createdDriverSettlements.totalAmount > 0 &&
            ` ($${createdDriverSettlements.totalAmount.toFixed(2)})`}
        </Typography>
      )}

      {skippedLines.map(({ count, reason }) => (
        <Typography
          key={reason}
          data-test-id="settlements-created-skipped-lines"
          variant="body2"
        >
          {t('approvals.driver_pay.settlements.bulk_create_settlement.skipped_lines', {
            count,
            reason: getLineIneligibilityReasonLabel(reason),
          })}
        </Typography>
      ))}

      {totalCount > 0 && onViewSettlements && (
        <SmallButton
          color="brandV2Green"
          onClick={(event) => {
            event.stopPropagation();
            onViewSettlements();
            onClose();
          }}
          size="small"
          sx={{ mt: 1 }}
          variant="contained"
        >
          {t('approvals.driver_pay.settlements.view_settlements')}
        </SmallButton>
      )}
    </Box>
  );
}
